<template>
  <div>
    <div v-if="!showNewRuleForm">
      <form @submit.prevent="submitted">
        <text-field
          id="name"
          label="Name"
          placeholder="The name of this section."
          v-model="v$.name.$model"
          :disabled="editing"
        >
          <template v-slot:validation v-if="v$.name.$error">
            <validation-error v-if="!v$.name.required"
              >You must specify a name.</validation-error
            >
          </template>
        </text-field>
        <rule-selector
          v-if="currentTemplates.length > 0"
          v-model="businessRuleId"
          @new-rule="showNewRuleForm = true"
        ></rule-selector>
        <form-buttons>
          <button class="btn btn-primary" :disabled="v$.$invalid">
            {{ editing ? "Save" : "Create" }}
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            @click="cancelClicked"
          >
            Cancel
          </button>
        </form-buttons>
      </form>
    </div>
    <div v-if="showNewRuleForm">
      <h3>New Display Rule</h3>
      <rule-form
        @save="newRuleCreated"
        @cancel="showNewRuleForm = false"
      ></rule-form>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import currentTempates from "@/mixins/currentTemplates";
import documentModelMixin from "@/mixins/documentModel";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import sections from "@/services/word/sectionsService";
import RuleSelector from "@/components/business-rules/RuleSelector.vue";
import RuleForm from "@/components/business-rules/RuleForm.vue";
import ValidationError from "../../components/common/forms/ValidationError.vue";
import { Section } from "@/models/documentModel";

const newRuleCreated = function (newRule) {
  this.businessRuleId = newRule.id;
  this.showNewRuleForm = false;
};

const cancelClicked = function () {
  this.$emit("cancel");
};

const submitted = async function () {
  this.v$.$touch();

  if (this.v$.$invalid) {
    this.submitStatus = "ERROR";
  } else {
    this.submitStatus = "PENDING";

    const model = this.documentModel;
    let section;

    if (!this.editing) {
      this.logDebug("Creating new section.", {
        name: this.name,
        businessRuleId: this.businessRuleId,
      });

      const { id, parentId } = await sections.add(this.name, this.boundingBoxesHidden);
      section = new Section(id, this.name, this.businessRuleId);

      if (parentId) {
        const parent = model.findContentControl(parentId);
        parent.contentControls.push(section);
      } else {
        model.contentControls.push(section);
      }

      this.logDebug("Added section to document model", section);
    } else {
      this.logDebug("Updating existing section.", { id: this.id });
      section = model.findContentControl(this.id);
      section.displayRuleId = this.businessRuleId;
    }

    await this.saveDocumentModel(model);
    this.logDebug("Document model saved.", model);
    this.$emit("save", section);
  }
};

export default {
  setup() {
    return { v$: useVuelidate() }
  },
  components: { ValidationError, RuleSelector, RuleForm },
  mixins: [documentModelMixin, currentTempates],
  props: {
    section: {
      required: false,
    },
  },
  data: function () {
    return {
      id: null,
      name: "",
      businessRuleId: "",
      showNewRuleForm: false,
    };
  },
  validations() {
    return {
      name: {
        required,
      },
    };
  },
  computed: {
    ...mapState({
      boundingBoxesHidden: (state) => state.boundingBoxesHidden,
    }),
    editing: function () {
      return !!this.section;
    },
    businessRuleItems: function () {
      if (this.documentModel && this.documentModel.businessRules) {
        return this.documentModel.businessRules.map((r) => ({
          label: r.name,
          value: r.id,
        }));
      }

      return [];
    },
  },
  methods: {
    submitted,
    cancelClicked,
    newRuleCreated,
  },
  emits: ["save", "cancel"],
  mounted: function () {
    const section = this.section;
    if (section) {
      this.id = section.id;
      this.name = section.systemName;
      this.businessRuleId = section.displayRuleId;
    }
  },
};
</script>
<style lang="scss" scoped>
.business-rules {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 1.5rem;

  & > .rule-dropdown {
    flex-grow: 1;
  }
}
</style>
